"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { welcomeFocusIn, welcomeKeyDown, welcomeStartState, type WelcomeWords } from "./welcomeCopy";

/** Where the welcome listens: the window in the browser, a stand-in in the unit test. */
export interface WelcomeListenTarget {
  addEventListener(type: string, listener: (e: Event) => void, capture: boolean): void;
  removeEventListener(type: string, listener: (e: Event) => void, capture: boolean): void;
}

/** A welcome that is up and listening. leave() closes it (once); disarm() is unmount, and is not a leave. */
export interface WelcomeArm {
  readonly leave: () => void;
  readonly disarm: () => void;
}

/**
 * Review finding 13 (TA-1356 follow-up): deciding whether the welcome shows and starting to listen are ONE
 * synchronous step, so no key, pointer or focus can fall between them. The first build decided in one effect and
 * listened in the next, after a re-render: a field focused in that gap was never heard, and a leave() there still
 * left the Enter-holding capture listeners attached until React re-rendered (a 300 ms main-thread stall swallowed
 * the first real Enter 8 of 8). Here a leave removes the listeners before it returns, so the very next event is
 * the page's, whatever React is doing. While the welcome is up, Enter is held by it (TA-1324, welcomeKeyDown).
 *
 * Returns null, attaching nothing, when the welcome was passed or a field already has focus (welcomeStartState,
 * the field read by welcomeFocusIn's own rule).
 */
export function armWelcome({
  target,
  seen,
  active,
  onLeave,
}: {
  readonly target: WelcomeListenTarget;
  readonly seen: boolean;
  readonly active: unknown;
  readonly onLeave: () => void;
}): WelcomeArm | null {
  let typing = false;
  welcomeFocusIn(active, () => {
    typing = true;
  });
  if (welcomeStartState({ seen, typing }) === "gone") return null;

  let live = true;
  // Capture phase, so the welcome hears Enter before a form field underneath does and keeps it (welcomeKeyDown).
  const onKey = (e: Event) => welcomeKeyDown(e as KeyboardEvent, leave);
  const onPointer = () => leave();
  // TA-1353: a field taking focus (the palette's autofocus among them) means the person moved on (welcomeFocusIn).
  const onFocus = (e: Event) => welcomeFocusIn(e.target, leave);
  function disarm(): void {
    if (!live) return;
    live = false;
    target.removeEventListener("keydown", onKey, true);
    target.removeEventListener("pointerdown", onPointer, true);
    target.removeEventListener("focusin", onFocus, true);
  }
  function leave(): void {
    if (!live) return;
    disarm();
    onLeave();
  }
  target.addEventListener("keydown", onKey, true);
  target.addEventListener("pointerdown", onPointer, true);
  target.addEventListener("focusin", onFocus, true);
  return { leave, disarm };
}

/**
 * TA-1324, Kabir's entrance page (typed 2026-10-13): the site opens on a quiet full screen that greets the
 * person (by first name on the overview, plainly on the signed-out pages, see welcomeCopy.ts), waits for Enter
 * (or a tap, or any key), then fades slowly into the page underneath. Once per tab: sessionStorage (one per tab)
 * remembers it was passed, each greeting under its own key, and every read and write is guarded because storage
 * can be absent or throw. It never appears over a field the person is already typing in (welcomeStartState). The dashboard is rendered underneath the whole time, so the fade reveals a page that is
 * already there. Motion honours prefers-reduced-motion through the global rule (globals.css), which collapses the
 * transition to an instant.
 *
 * The veil never takes a pointer event: it is a picture over a live page, so a click or tap anywhere lands on the
 * dashboard as it would without the veil and dismisses it at the same time, and automation that drives the page
 * (the e2e walk) is never blocked by it. The first gate shipped as a modal and timed out every e2e test that
 * clicked the overview. Only the "Press Enter" button inside the veil is clickable.
 *
 * The greeting is a paragraph, not a heading: the page's one h1 belongs to the overview underneath, and a second
 * would sit in the outline (and in every getByRole("heading") query) after the veil has faded.
 */
export function WelcomeGate({ heading, lead, seenKey }: WelcomeWords) {
  // "pending": server render and first client frame, invisible, so a returning visitor never sees a flash.
  // "shown": fading in and waiting. "leaving": fading out. "gone": unmounted.
  const [state, setState] = useState<"pending" | "shown" | "leaving" | "gone">("pending");
  const armed = useRef<WelcomeArm | null>(null);

  // Review finding 13: one effect decides AND listens (armWelcome), so there is no gap between the two.
  useEffect(() => {
    let seen = false;
    try {
      seen = window.sessionStorage.getItem(seenKey) === "1";
    } catch {
      seen = false;
    }
    const arm = armWelcome({
      target: window,
      seen,
      active: document.activeElement,
      onLeave: () => {
        try {
          window.sessionStorage.setItem(seenKey, "1");
        } catch {
          // Storage absent: the screen shows again next time, which is the harmless direction.
        }
        setState((s) => (s === "shown" ? "leaving" : s));
      },
    });
    armed.current = arm;
    setState(arm === null ? "gone" : "shown");
    return () => {
      arm?.disarm();
      if (armed.current === arm) armed.current = null;
    };
  }, [seenKey]);

  const leave = useCallback(() => armed.current?.leave(), []);

  useEffect(() => {
    if (state !== "leaving") return;
    const t = window.setTimeout(() => setState("gone"), 1500);
    return () => window.clearTimeout(t);
  }, [state]);

  if (state === "gone") return null;

  const visible = state === "shown";
  return (
    <div
      aria-hidden={!visible}
      data-testid="welcome-gate"
      data-state={state}
      className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-surface)] px-4"
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${state === "leaving" ? "1.4s" : "0.9s"} ease-in-out`,
      }}
    >
      <div className="text-center">
        <p className="font-bold text-[length:var(--text-2xl)] leading-tight text-[var(--color-ink)]">
          {heading}
        </p>
        <p className="mt-3 text-[length:var(--text-base)] text-[var(--color-ink-muted)]">
          {lead}
        </p>
        <button
          type="button"
          className="pointer-events-auto mt-8 text-[length:var(--text-sm)] text-[var(--color-ink-muted)] underline-offset-4 hover:underline"
          tabIndex={visible ? 0 : -1}
          data-testid="welcome-begin"
          onClick={leave}
        >
          Press Enter to begin
        </button>
      </div>
    </div>
  );
}
